import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Alert, Box, Tooltip } from '@mui/material';
import ButtonCustom from 'components/ButtonCustom';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRevisionByIdWithProject } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { IRevisionWithProjectResponse } from 'utils/contracts';
import ProjectBanner from '../components/ProjectBanner';
import UseAuthContext from '../context/AuthContext';

const IframeShgcViewer: React.FC = () => {
  const { isAuthenticated, auth, organizationId, role } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const revisionId = searchParams.get('revisionId');
  const projectId = searchParams.get('projectId');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isExportReady, setIsExportReady] = useState(false);
  const { data: revisionWithProjectData, isLoading } = useQuery<IRevisionWithProjectResponse>(
    ['getRevisionByIdWithProject', revisionId, projectId],
    () => getRevisionByIdWithProject(revisionId, projectId, organizationId),
    {
      enabled: !!revisionId && !!projectId && (!!organizationId || role === USER_ROLES.INSPECTOR || role === USER_ROLES.INSPECTOR_MANAGER || role === USER_ROLES.ADMIN)
    }
  );

  const postToIframe = (message: Record<string, unknown>) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  };

  const handleIframeLoad = () => {
    if (!iframeRef.current) return;
    setIsExportReady(false);

    if (auth?.token) {
      postToIframe({ type: 'SET_TOKEN', token: auth.token, organizationId: organizationId || null });
      setTimeout(() => postToIframe({ type: 'SET_TOKEN', token: auth.token, organizationId: organizationId || null }), 500);
    }

    if (revisionWithProjectData?.project_type) {
      postToIframe({
        type: 'SET_REVISION_CONTEXT',
        projectType: revisionWithProjectData.project_type
      });
    }
  };

  useEffect(() => {
    if (revisionWithProjectData?.project_type && iframeRef.current?.contentWindow) {
      postToIframe({
        type: 'SET_REVISION_CONTEXT',
        projectType: revisionWithProjectData.project_type
      });
    }
  }, [revisionWithProjectData?.project_type]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_HOST || 'http://localhost:3000'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data.type === 'TOKEN_SET') {
        console.log('Token successfully set in SHGC viewer');
      }
      if (event.data.type === 'SHGC_EXPORT_READY') {
        setIsExportReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleDownloadExcel = () => {
    postToIframe({ type: 'SHGC_EXPORT_EXCEL' });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search;
      navigate('/login', { state: { redirect: currentPath } });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    if (revisionWithProjectData && organizationId && role !== USER_ROLES.ADMIN && role !== USER_ROLES.INSPECTOR && role !== USER_ROLES.INSPECTOR_MANAGER) {
      const revisionOrganizationId = revisionWithProjectData.organization_id;
      if (organizationId !== revisionOrganizationId) {
        console.error('Access denied: User does not belong to the same organization as the revision');
      }
    }
  }, [revisionWithProjectData, organizationId, role]);

  if (!code) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">
          No revision code provided. Please add ?code=YOUR_CODE to the URL.
        </Alert>
      </Box>
    );
  }

  if (revisionWithProjectData && organizationId && organizationId !== revisionWithProjectData.organization_id && role !== USER_ROLES.ADMIN && role !== USER_ROLES.INSPECTOR && role !== USER_ROLES.INSPECTOR_MANAGER) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Alert severity="error">
          {t('3D_VIEWER_ACCESS_DENIED')}
        </Alert>
        <ButtonCustom variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('3D_VIEWER_BUTTON_BACK_TO_PROJECTS')}
        </ButtonCustom>
      </Box>
    );
  }

  const backendHost = process.env.REACT_APP_HOST || 'http://localhost:3000';
  const orgQuery = organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '';
  const viewerUrl = `${backendHost}/idf/shgc?code=${code}${orgQuery}`;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ProjectBanner
        isLoading={isLoading}
        revisionWithProjectData={revisionWithProjectData}
        headerActions={
          <Tooltip title={t('SHGC_DOWNLOAD_EXCEL_BUTTON')} placement="bottom">
            <span>
              <ButtonCustom
                variant="contained"
                disabled={!isExportReady}
                onClick={handleDownloadExcel}
                sx={{
                  minWidth: '56px',
                  width: '56px',
                  height: '56px',
                  borderRadius: '8px',
                  px: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textTransform: 'none',
                  bgcolor: '#16a34a',
                  '&:hover': {
                    bgcolor: '#15803d'
                  }
                }}
                aria-label={t('SHGC_DOWNLOAD_EXCEL_BUTTON')}
              >
                <FileDownloadOutlinedIcon />
              </ButtonCustom>
            </span>
          </Tooltip>
        }
      />

      <Box sx={{ flexGrow: 1, marginTop: '80px', overflow: 'auto' }}>
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          width="100%"
          height="100%"
          title={`${t('SHGC_VIEWER_BUTTON')} Viewer`}
          style={{
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            minHeight: 'calc(100vh - 80px)'
          }}
          onLoad={handleIframeLoad}
        />
      </Box>
    </Box>
  );
};

export default IframeShgcViewer;
